// The join screen. The customer arrives from WhatsApp: greeted by name, told whose meeting and when, sees themself,
// hears their microphone move the meter, and joins with one button. Nothing is sent until they press Join.
import { createLocalTracks, Room, VideoPresets } from "livekit-client";
import { firstName, h, initials, modal, toast } from "./dom.js";
import { icon } from "./icons.js";
import { formatWhen, huddleClosed } from "./format.js";
import { isCanvas, product, words, wordmark } from "./product.js";
import { pickBackground, restoreBackground } from "./bgpicker.js";
import { saved as savedBackground, settled as backgroundSettled, supported as backgroundsHere } from "./background.js";
import { paintWaitlist, watchDoor } from "./door.js";
import { legalText } from "./legal.js";
import { meetingWords, plainCallAssistNote } from "./host-words.js";

// A link exists from the booking, but the room opens only around the meeting's own time (an hour before to three
// hours after), and never once it was cancelled or the sales manager ended it. The join page says which, plainly.
function ready(joinBtn, label, info, showError) {
  const w = info.window || { state: "open" };
  if (w.state === "open") {
    joinBtn.disabled = false;
    joinBtn.textContent = label;
    if (info.notice) showError(info.notice); // why the last Join was refused
    return;
  }
  joinBtn.disabled = true;
  const closed = huddleClosed(info.meeting || {}, w);
  if (closed) { joinBtn.textContent = closed.button; showError(closed.line); return; }
  joinBtn.textContent = { early: "Not open yet", over: "This meeting is over", ended: "This meeting has ended", cancelled: "This meeting was cancelled" }[w.state] || label;
  showError({
    early: `Your meeting opens at ${formatWhen(w.opensAt)} — an hour before it starts. This same link will work then.`,
    over: `This meeting is over. ${words.askAgain} if you would like another.`,
    ended: `This meeting has ended. ${words.askAgain} if you would like another.`,
    cancelled: `This meeting was cancelled. ${words.askAgain} for a new time.`,
  }[w.state]);
}

// opts (ClikMeet's room page, CR-20260926-001 U4): joinLabel for the button ("Ask to join"), extra — what sits under it
// (the ClikWork sign-in) — and nameMissing, what is said when no name was typed.
export function lobby(root, info, opts = {}) {
  const joinLabel = opts.joinLabel || "Join meeting";
  return new Promise((resolve) => {
    const { meeting, you } = info;
    const isHost = you.role === "host";
    const state = { micOn: true, camOn: true, audioTrack: null, videoTrack: null, audioOutput: "", meterStop: null };

    const video = h("video", { autoplay: true, playsinline: true, muted: true });
    const offBox = h("div", { class: "off" });
    const nameChip = h("div", { class: "nm", text: you.name || "You" });
    const meter = h("div", { class: "meter", "aria-hidden": "true" }, Array.from({ length: 5 }, () => h("i")));
    const preview = h("div", { class: "preview" }, video, offBox, nameChip, meter);

    const micBtn = h("button", { class: "round", type: "button", "aria-label": "Turn microphone off", onclick: () => setMic(!state.micOn) });
    const camBtn = h("button", { class: "round", type: "button", "aria-label": "Turn camera off", onclick: () => setCam(!state.camOn) });
    const setBtn = h("button", { class: "round", type: "button", "aria-label": "Choose microphone, camera and speaker", icon: "settings", onclick: () => settings() });
    // ClikMeet: blur or a picture behind you, chosen before joining (Canvas has none).
    const bgBtn = !isCanvas && backgroundsHere()
      ? h("button", { class: "round", type: "button", "aria-label": "Background and lighting", icon: "background", onclick: () => pickBackground(() => state.videoTrack) })
      : null;
    const toggles = h("div", { class: "toggles" }, micBtn, camBtn, bgBtn, setBtn);

    const nameField = you.needsName
      ? h("div", { class: "field" }, h("label", { for: "ms-name", text: "Your name" }),
          h("input", { id: "ms-name", autocomplete: "name", maxlength: "60", placeholder: "So everyone knows who has joined" }))
      : null;
    const errorBox = h("div", { class: "notice", role: "alert", hidden: true });
    const lobbyDoor = info.room ? h("section", { class: "waitlist lobby-door", "aria-label": "Waiting to join", hidden: true }) : null;
    const doorWatch = lobbyDoor ? watchDoor(you.code, (ks) => paintWaitlist(lobbyDoor, ks, doorWatch)) : null;
    // Join waits for the camera and microphone to be asked for: pressing it sooner joined with neither.
    const joinBtn = h("button", { class: "btn btn-join", type: "button", text: "Checking your camera…", disabled: true, onclick: () => join() });

    const eyebrow = words.eyebrow(meeting);
    const statement = isHost ? meeting.title : words.statement(meeting);
    const card = h("dl", { class: "meeting-card" },
      h("div", {}, h("dt", { text: "With" }), h("dd", { text: words.withLine(meeting, isHost) })),
      h("div", {}, h("dt", { text: "When" }), h("dd", { text: `${formatWhen(meeting.startsAt)} · ${meeting.durationMin} minutes` })),
      meeting.project ? h("div", {}, h("dt", { text: "Project" }), h("dd", { text: meeting.project })) : null);

    const copy = h("div", { class: "lobby-copy" },
      h("p", { class: "eyebrow", text: eyebrow }),
      h("h1", { class: "statement", text: statement }),
      h("p", { class: "lede", text: isHost ? "Check your camera and microphone, then open the room." : `Hello${you.name ? ", " + firstName(you.name) : ""}. Check how you look and sound, then join when you are ready.` }),
      // Join sits with the greeting, so on a phone it is on the first screen; the details follow.
      nameField, errorBox, joinBtn, ...(opts.extra || []),
      // Whoever keeps a ClikMeet room's door sees who is waiting from here too, and can let them in before joining (F6).
      lobbyDoor,
      // Ravi, 26 Sep: no separate transcription notice — his recording sentence covers it once recording is
        // on (D145, audio only), and he is keeping recording. Until then live assist transcribes and
        // nothing here says so; that window is his call, made knowingly.
        meeting.recorded ? h("p", { class: "notice", text: "We record this meeting to improve customer experience and serve you better." }) : null,
      // ClikMeet M4 (D135 A): said before anyone joins, as the recording line is on Canvas.
      meeting.transcribe ? h("p", { class: "notice", text: "This call is transcribed, so everyone gets a summary afterwards. The host can turn it off." }) : null,
      plainCallAssistNote(meeting, isHost) ? h("p", { class: "notice", text: plainCallAssistNote(meeting, isHost) }) : null,
      // D137 A: while the host is recording, anyone about to join is told so first.
      meeting.recording ? h("p", { class: "notice notice-rec", text: "The host is recording this call (video and sound)." }) : null,
      card,
      h("p", { class: "fine", text: "No app needed. Your camera and microphone stay with you until you press Join." }));

    const page = h("div", { class: "page" },
      h("header", { class: "page-head" }, wordmark()),
      h("main", { class: "page-main" }, h("div", { class: "lobby" }, h("div", { class: "lobby-stage" }, preview, toggles), copy)));
    root.replaceChildren(page);
    document.title = product.title;
    paint();
    start();

    async function start() {
      // A meeting that cannot be joined asks for neither camera nor microphone.
      if ((info.window?.state || "open") !== "open") { state.micOn = false; state.camOn = false; state.closed = true; ready(joinBtn, joinLabel, info, showError); paint(); return; }
      try {
        const tracks = await createLocalTracks({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: { resolution: VideoPresets.h720.resolution, facingMode: "user" },
        });
        for (const t of tracks) {
          if (t.kind === "audio") state.audioTrack = t;
          if (t.kind === "video") state.videoTrack = t;
        }
        if (state.videoTrack) state.videoTrack.attach(video);
        if (state.audioTrack) meterOn(state.audioTrack.mediaStreamTrack);
        if (!isCanvas) restoreBackground(state.videoTrack);
      } catch (err) {
        // Asked together, one missing, busy or blocked device fails both (one getUserMedia). Ask for each alone, keep
        // what works, and say which one did not — a desk without a webcam still joins audible (audit F3).
        let audioErr = null, videoErr = null;
        try { const [t] = await createLocalTracks({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false }); state.audioTrack = t; }
        catch (e) { audioErr = e; }
        try { const [t] = await createLocalTracks({ audio: false, video: { resolution: VideoPresets.h720.resolution, facingMode: "user" } }); state.videoTrack = t; }
        catch (e) { videoErr = e; }
        state.micOn = Boolean(state.audioTrack);
        state.camOn = Boolean(state.videoTrack);
        if (state.videoTrack) state.videoTrack.attach(video);
        if (state.audioTrack) meterOn(state.audioTrack.mediaStreamTrack);
        if (!isCanvas) restoreBackground(state.videoTrack);
        const blocked = (e) => e && e.name === "NotAllowedError";
        if (audioErr && videoErr) {
          showError(blocked(audioErr) || blocked(err)
            ? "Your browser is blocking the camera and microphone. Allow them for this site (the icon beside the address), or join and just listen."
            : "We could not reach your camera or microphone. You can still join and listen.");
        } else if (videoErr) {
          showError(blocked(videoErr) ? "Your browser is blocking the camera — you will join with your microphone on and your camera off."
            : "We could not reach your camera (another app may be using it) — you will join with your microphone on and your camera off.");
        } else if (audioErr) {
          showError(blocked(audioErr) ? "Your browser is blocking the microphone — you will join with your camera, and nobody will hear you."
            : "We could not reach your microphone — you will join with your camera, and nobody will hear you.");
        }
      }
      ready(joinBtn, joinLabel, info, showError);
      paint();
    }

    function meterOn(mediaTrack) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const src = ctx.createMediaStreamSource(new MediaStream([mediaTrack]));
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        src.connect(an);
        const buf = new Uint8Array(an.fftSize);
        const bars = [...meter.children];
        let raf = 0;
        const tick = () => {
          an.getByteTimeDomainData(buf);
          let peak = 0;
          for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
          const level = state.micOn ? Math.min(1, peak / 60) : 0;
          bars.forEach((b, i) => {
            const on = level > (i + 1) / (bars.length + 1);
            b.classList.toggle("on", on);
            b.style.height = on ? `${6 + i * 3}px` : "4px";
          });
          raf = requestAnimationFrame(tick);
        };
        tick();
        state.meterStop = () => { cancelAnimationFrame(raf); ctx.close(); };
      } catch { /* a meter is a nicety */ }
    }

    async function setMic(on) {
      if (state.closed) return; // a meeting that cannot be joined opens neither
      state.micOn = on && Boolean(state.audioTrack);
      if (state.audioTrack) on ? await state.audioTrack.unmute() : await state.audioTrack.mute();
      paint();
    }
    async function setCam(on) {
      if (state.closed) return; // a meeting that cannot be joined opens neither
      state.camOn = on && Boolean(state.videoTrack);
      if (state.videoTrack) on ? await state.videoTrack.unmute() : await state.videoTrack.mute();
      paint();
    }

    function paint() {
      micBtn.innerHTML = icon(state.micOn ? "mic" : "micOff");
      camBtn.innerHTML = icon(state.camOn ? "video" : "videoOff");
      micBtn.classList.toggle("is-off", !state.micOn);
      camBtn.classList.toggle("is-off", !state.camOn);
      micBtn.setAttribute("aria-label", state.micOn ? "Turn microphone off" : "Turn microphone on");
      camBtn.setAttribute("aria-label", state.camOn ? "Turn camera off" : "Turn camera on");
      video.hidden = !state.camOn;
      offBox.replaceChildren(...(state.camOn ? [] : [h("div", {}, h("div", { class: "avatar", text: initials(you.name || "You") }), h("div", { text: "Your camera is off" }))]));
      meter.hidden = !state.micOn;
    }

    function showError(text) {
      errorBox.textContent = text;
      errorBox.hidden = !text;
    }

    async function settings() {
      const body = await deviceForm({
        audioinput: state.audioTrack?.mediaStreamTrack?.getSettings().deviceId,
        videoinput: state.videoTrack?.mediaStreamTrack?.getSettings().deviceId,
        audiooutput: state.audioOutput,
      }, async (kind, id) => {
        if (kind === "audioinput" && state.audioTrack) await state.audioTrack.restartTrack({ deviceId: id });
        if (kind === "videoinput" && state.videoTrack) await state.videoTrack.restartTrack({ deviceId: id });
        if (kind === "audiooutput") state.audioOutput = id;
      });
      await modal("Your devices", body, [{ text: "Done", value: true, class: "btn" }]);
    }

    async function join() {
      const name = you.needsName ? String(nameField.querySelector("input").value || "").trim() : you.name;
      if (!name) { showError(opts.nameMissing || "Please tell us your name first."); nameField.querySelector("input").focus(); return; }
      showError("");
      joinBtn.disabled = true;
      joinBtn.textContent = "Joining…";
      // ClikMeet: a background being put on the camera is finished before joining — the room behind you is never sent
      // (re-audit N1). If it cannot finish in 20 s, you join with the camera off rather than without your background.
      if (!isCanvas && state.videoTrack && state.camOn && backgroundsHere() && savedBackground().id !== "none") {
        const done = await Promise.race([backgroundSettled(state.videoTrack).then(() => true), new Promise((r) => setTimeout(() => r(false), 20_000))]);
        if (!done || !state.videoTrack.getProcessor?.()) {
          state.camOn = false;
          await state.videoTrack.mute().catch(() => {});
          paint();
          toast("Your background did not load in time, so you joined with your camera off. Turn it on when you are ready.");
        }
      }
      state.meterStop?.();
      doorWatch?.stop(); // the call keeps the door from here
      if (state.videoTrack) state.videoTrack.detach(video);
      resolve({
        name,
        micOn: state.micOn,
        camOn: state.camOn,
        audioTrack: state.audioTrack,
        videoTrack: state.videoTrack,
        audioOutput: state.audioOutput,
        fail(message) { // the call could not start: back to this screen, tracks kept
          joinBtn.disabled = false;
          joinBtn.textContent = joinLabel;
          if (state.videoTrack) state.videoTrack.attach(video);
          showError(message);
        },
      });
    }
  });
}

// Microphone, camera and speaker pickers — used here and inside the call.
export async function deviceForm(current, onChange) {
  const kinds = [["audioinput", "Microphone"], ["videoinput", "Camera"]];
  if ("setSinkId" in HTMLMediaElement.prototype) kinds.push(["audiooutput", "Speaker"]);
  const wrap = h("div");
  for (const [kind, label] of kinds) {
    let devices = [];
    try { devices = await Room.getLocalDevices(kind, false); } catch { /* none */ }
    if (!devices.length) continue;
    const select = h("select", { id: `ms-${kind}`, onchange: (e) => onChange(kind, e.target.value) },
      devices.map((d, i) => h("option", { value: d.deviceId, selected: d.deviceId === current[kind] || false, text: d.label || `${label} ${i + 1}` })));
    wrap.append(h("div", { class: "field" }, h("label", { for: `ms-${kind}`, text: label }), select));
  }
  if (!wrap.children.length) wrap.append(h("p", { class: "fine", text: "No devices to choose from yet — allow the camera and microphone first." }));
  return wrap;
}

// The customer's join, for a meeting with a canvas: the project's own screen, not a device check. Who is waiting
// for them, their microphone on and their camera off (asked for only if they turn it on), their language, and the
// code they can read out if the link ever breaks.
export function customerLobby(root, info, { hero, langs }) {
  return new Promise((resolve) => {
    const { meeting, you } = info;
    const host = meeting.hostName;
    const words = meetingWords(meeting);
    const state = { micOn: true, camOn: false, audioTrack: null, videoTrack: null, lang: "en", meterStop: null };
    const status = info.hostPresent === true ? `${firstName(host)} is waiting for you`
      : info.hostPresent === false ? `${firstName(host)} will meet you here · ${formatWhen(meeting.startsAt)}`
      : words.hostLine(host);
    const video = h("video", { autoplay: true, playsinline: true, muted: true });
    const selfBox = h("div", { class: "cl-self", hidden: true }, video);
    const meter = h("div", { class: "meter cl-meter", "aria-hidden": "true" }, Array.from({ length: 5 }, () => h("i")));
    const micTg = h("button", { class: "cl-tg", type: "button", onclick: () => setMic(!state.micOn) });
    const camTg = h("button", { class: "cl-tg", type: "button", onclick: () => setCam(!state.camOn) });
    const nameField = you.needsName ? h("input", { class: "cl-name", maxlength: "60", autocomplete: "name", placeholder: "Your name", "aria-label": "Your name" }) : null;
    const errorBox = h("p", { class: "notice", role: "alert", hidden: true });
    const joinBtn = h("button", { class: "btn cl-go", type: "button", text: "Checking your microphone…", disabled: true, onclick: () => join() });
    const langRow = h("div", { class: "cl-langs" });
    const page = h("div", { class: "cl" },
      h("div", { class: "cl-hero", style: `background-image:url('${hero}')` },
        h("div", { class: "cl-ht" }, h("div", { class: "k", text: words.eyebrow }), h("div", { class: "t", text: meeting.project || meeting.title }))),
      h("div", { class: "cl-body" },
        h("div", { class: "host" }, h("div", { class: "face" + (info.hostPresent ? " live" : ""), text: initials(host) }),
          h("div", {}, h("b", { text: status }), h("small", { text: words.hostLabel }))),
        h("p", { class: "cl-when", text: `${formatWhen(meeting.startsAt)} · about ${meeting.durationMin} minutes` }),
        h("div", { class: "cl-tgs" }, micTg, camTg), selfBox, meter,
        h("p", { class: "cl-note", text: "Your camera is off — turn it on only if you'd like to. The tour works either way." }),
        // Ravi, 26 Sep: no separate transcription notice — his recording sentence covers it once recording is
        // on (D145, audio only), and he is keeping recording. Until then live assist transcribes and
        // nothing here says so; that window is his call, made knowingly.
        meeting.recorded ? h("p", { class: "notice", text: "We record this meeting to improve customer experience and serve you better." }) : null,
        meeting.assist ? h("p", { class: "cl-note cl-assist", text: words.assistNote(firstName(host)) }) : null,
        nameField, errorBox, joinBtn, langRow,
        you.displayCode ? h("p", { class: "cl-code" }, "Meeting code ", h("b", { text: you.displayCode }), " · with the last four digits of your phone") : null,
        // g-legal-line (CR-20261009-001): who stands behind ClikHome, at the foot — text only, never markup
        h("p", { class: "legal", text: legalText(info) })));
    root.replaceChildren(page);
    document.title = "Walkin · Clikin.ai";
    paint();
    start();

    async function start() {
      // A meeting that cannot be joined asks for neither camera nor microphone.
      if ((info.window?.state || "open") !== "open") { state.micOn = false; state.camOn = false; state.closed = true; ready(joinBtn, "Join the tour", info, showError); paint(); return; }
      try {
        const [track] = await createLocalTracks({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        state.audioTrack = track;
        meterStart(track.mediaStreamTrack);
      } catch (err) {
        state.micOn = false;
        showError(err && err.name === "NotAllowedError"
          ? "Your browser is blocking the microphone. Allow it for this site (the icon beside the address), or join and just listen."
          : "We could not reach your microphone. You can still join and listen.");
      }
      ready(joinBtn, "Join the tour", info, showError);
      paint();
    }
    function meterStart(mediaTrack) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        ctx.createMediaStreamSource(new MediaStream([mediaTrack])).connect(an);
        const buf = new Uint8Array(an.fftSize);
        const bars = [...meter.children];
        let raf = 0;
        const tick = () => {
          an.getByteTimeDomainData(buf);
          let peak = 0;
          for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
          const level = state.micOn ? Math.min(1, peak / 60) : 0;
          bars.forEach((b, i) => { const on = level > (i + 1) / (bars.length + 1); b.classList.toggle("on", on); b.style.height = on ? `${6 + i * 3}px` : "4px"; });
          raf = requestAnimationFrame(tick);
        };
        tick();
        state.meterStop = () => { cancelAnimationFrame(raf); ctx.close(); };
      } catch { /* a meter is a nicety */ }
    }
    async function setMic(on) {
      if (state.closed) return; // a meeting that cannot be joined opens neither
      state.micOn = on && Boolean(state.audioTrack);
      if (state.audioTrack) on ? await state.audioTrack.unmute() : await state.audioTrack.mute();
      paint();
    }
    async function setCam(on) {
      if (state.closed) return; // a meeting that cannot be joined opens neither
      if (on && !state.videoTrack) {
        try {
          const [track] = await createLocalTracks({ video: { resolution: VideoPresets.h720.resolution, facingMode: "user" } });
          state.videoTrack = track;
        } catch { showError("Your camera is blocked in this browser — you can join without it."); return; }
      }
      state.camOn = on && Boolean(state.videoTrack);
      if (state.videoTrack) {
        if (state.camOn) { await state.videoTrack.unmute(); state.videoTrack.attach(video); } else { await state.videoTrack.mute(); }
      }
      paint();
    }
    function paint() {
      micTg.replaceChildren(h("b", { text: state.micOn ? "Microphone on" : "Microphone off" }), h("span", { text: state.micOn ? `So ${firstName(host)} can hear you` : "Tap to turn it on" }));
      micTg.insertAdjacentHTML("afterbegin", icon(state.micOn ? "mic" : "micOff"));
      micTg.classList.toggle("off", !state.micOn);
      camTg.replaceChildren(h("b", { text: state.camOn ? "Camera on" : "Camera off" }), h("span", { text: state.camOn ? "Tap to turn it off" : "Turn it on only if you like" }));
      camTg.insertAdjacentHTML("afterbegin", icon(state.camOn ? "video" : "videoOff"));
      camTg.classList.toggle("off", !state.camOn);
      selfBox.hidden = !state.camOn;
      meter.hidden = !state.micOn;
      langRow.replaceChildren(...langs.map(([k, n]) => h("button", { type: "button", class: state.lang === k ? "on" : "", text: n, onclick: () => { state.lang = k; paint(); } })));
    }
    function showError(text) { errorBox.textContent = text; errorBox.hidden = !text; }
    function join() {
      const name = you.needsName ? String(nameField.value || "").trim() : you.name;
      if (!name) { showError("Please tell us your name first."); nameField.focus(); return; }
      showError("");
      joinBtn.disabled = true;
      joinBtn.textContent = "Joining…";
      state.meterStop?.();
      if (state.videoTrack) state.videoTrack.detach(video);
      resolve({
        name, lang: state.lang, micOn: state.micOn, camOn: state.camOn, audioTrack: state.audioTrack, videoTrack: state.videoTrack, audioOutput: "",
        fail(message) { joinBtn.disabled = false; joinBtn.textContent = "Join the tour"; showError(message); },
      });
    }
  });
}
